import React, { useState } from "react";

const ClientPersonalInfo = ({ userData, setUserData }) => {
    const [isEditing, setIsEditing] = useState(false);
    const [formData, setFormData] = useState({
        name: userData.name,
        email: userData.email,
    });

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: value
        }));
    };
    const handleSubmit = (e) => {
        e.preventDefault();
        setUserData(prev => ({
            ...prev,
            name: formData.name,
            email: formData.email,
        }));
        setIsEditing(false);
        setFormData(prev => ({
            ...prev,
            currentPassword: "",
            newPassword: "",
            confirmPassword: ""
        }));
    };


    return (
        <div>
            <div className="flex items-center justify-between mb-8">
                <div>
                    <h2 className="text-2xl font-bold text-gray-900">Personal Information</h2>
                    <p className="text-gray-600 mt-1">Manage your account details and profile settings</p>
                </div>
            </div>

            <form onSubmit={handleSubmit} className="space-y-6">
                <div className="w-full">
                    <label htmlFor="name" className="block text-sm font-medium text-gray-700 mb-2">
                        Full Name *
                    </label>
                    <input
                        type="text"
                        id="name"
                        name="name"
                        value={formData.name}
                        onChange={handleInputChange}
                        disabled={!isEditing}
                        className={`w-full px-4 py-3 border rounded-lg transition-all duration-200 ${isEditing
                            ? "border-gray-300 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                            : "border-gray-200 bg-gray-50"
                            }`}
                        placeholder="Enter your full name"
                    />
                </div>

                {/* Email Field */}
                <div className="w-full">
                    <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-2">
                        Email Address *
                    </label>
                    <input
                        type="email"
                        id="email"
                        name="email"
                        value={formData.email}
                        onChange={handleInputChange}
                        disabled={!isEditing}
                        className={`w-full px-4 py-3 border rounded-lg transition-all duration-200 ${isEditing
                            ? "border-gray-300 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                            : "border-gray-200 bg-gray-50"
                            }`}
                        placeholder="Enter your email address"
                    />
                </div>

                {/* Firm Details Section (for Law Firms) */}
                {userData.isLawFirm && (
                    <div className="border-t pt-6">
                        <h3 className="text-lg font-semibold text-gray-900 mb-4">Firm Details</h3>

                        {/* Contact Number */}
                        <div className="w-full mb-6">
                            <label htmlFor="contactNumber" className="block text-sm font-medium text-gray-700 mb-2">
                                Contact Number *
                            </label>
                            <input
                                type="tel"
                                id="contactNumber"
                                name="contactNumber"
                                value={formData.contactNumber}
                                onChange={handleInputChange}
                                disabled={!isEditing}
                                className={`w-full px-4 py-3 border rounded-lg transition-all duration-200 ${isEditing
                                    ? "border-gray-300 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                    : "border-gray-200 bg-gray-50"
                                    }`}
                                placeholder="Enter contact number"
                            />
                        </div>

                        {/* Address */}
                        <div className="w-full mb-6">
                            <label htmlFor="address" className="block text-sm font-medium text-gray-700 mb-2">
                                Firm Address *
                            </label>
                            <textarea
                                id="address"
                                name="address"
                                value={formData.address}
                                onChange={handleInputChange}
                                disabled={!isEditing}
                                rows={3}
                                className={`w-full px-4 py-3 border rounded-lg transition-all duration-200 ${isEditing
                                    ? "border-gray-300 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                    : "border-gray-200 bg-gray-50"
                                    }`}
                                placeholder="Enter firm address"
                            />
                        </div>

                        {/* License Number */}
                        <div className="w-full mb-6">
                            <label htmlFor="licenseNumber" className="block text-sm font-medium text-gray-700 mb-2">
                                Bar License Number *
                            </label>
                            <input
                                type="text"
                                id="licenseNumber"
                                name="licenseNumber"
                                value={formData.licenseNumber}
                                onChange={handleInputChange}
                                disabled={!isEditing}
                                className={`w-full px-4 py-3 border rounded-lg transition-all duration-200 ${isEditing
                                    ? "border-gray-300 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                    : "border-gray-200 bg-gray-50"
                                    }`}
                                placeholder="Enter bar license number"
                            />
                        </div>

                        {/* Firm Specialties */}
                        <div className="w-full mb-6">
                            <label className="block text-sm font-medium text-gray-700 mb-2">
                                Practice Areas
                            </label>
                            <div className="flex flex-wrap gap-2">
                                {userData.firmDetails?.specialties?.map((specialty, index) => (
                                    <span
                                        key={index}
                                        className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-blue-100 text-blue-800"
                                    >
                                        {specialty}
                                    </span>
                                ))}
                            </div>
                        </div>
                    </div>
                )}

            </form>

            {/* Account Information */}
            <div className="mt-8 pt-6 border-t border-gray-200">
                <h3 className="text-lg font-semibold text-gray-900 mb-4">Account Information</h3>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                    <div className="bg-gray-50 rounded-lg p-4">
                        <h4 className="font-medium text-gray-900 mb-2">Account Type</h4>
                        <p className="text-gray-600">{userData.isLawFirm ? "Law Firm" : "Individual"}</p>
                    </div>
                    <div className="bg-gray-50 rounded-lg p-4">
                        <h4 className="font-medium text-gray-900 mb-2">Member Since</h4>
                        <p className="text-gray-600">January 2024</p>
                    </div>
                    <div className="bg-gray-50 rounded-lg p-4">
                        <h4 className="font-medium text-gray-900 mb-2">Last Login</h4>
                        <p className="text-gray-600">Today at 2:30 PM</p>
                    </div>
                    <div className="bg-gray-50 rounded-lg p-4">
                        <h4 className="font-medium text-gray-900 mb-2">Account Status</h4>
                        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                            Active
                        </span>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default ClientPersonalInfo;
